// kfe-recording-control-bar.js — the Self-Recording transport bar.
//
// iOS CANON (Rule #9 — mirror exactly):
//   KFE-4.0-App/iEnglish/Classes/CustomViews/KFERecordingVoiceControl/
//     KFERecordingSoundControlView.{h,m,xib}
//   driven by KFESelfRecordingVC.m (the per-state enable/label logic).
//
// IBOutlet inventory (KFERecordingSoundControlView.h:17-48):
//     pervOneBtn    (h:17)  ⏮ previous word        → pervOneBtnResponseBlock      (h:19)
//     playBtn       (h:21)  ▶ play the MODEL audio  → playBtnResponseBlock         (h:23)
//     recordBtn     (h:25)  ⏺ record                → recordBtnResponseBlock       (h:27)
//     playRecordBtn (h:29)  ▶ play YOUR recording   → playRecordBtnResponseBlock   (h:31)
//     nextOneBtn    (h:33)  ⏭ next word             → nextOneBtnResponseBlock      (h:35)
//     soundSlider   (h:37)  volume scrubber         → soundSliderValueChangeBlock  (h:39)
//     speedBtn      (h:41)  speed cycle             → speedBtnResponseBlock        (h:43)
//     numLabel      (h:46)  "Num:N/M"  (word index / total)
//     playSoFarLabel(h:48)  "Play so far:N/M"  (this word's plays / required)
//
// Label/enable rules mirrored from KFESelfRecordingVC.m:
//   numLabel       = "Num:{tag+1}/{allItemsCount}"          (VC.m:169-170)
//   playSoFarLabel = "Play so far:{playsSoFarCount}/{mustPlayCount}" (VC.m:173)
//   nextOneBtn.enabled = playsSoFarCount >= mustPlayCount    (VC.m:175-181)
//   playRecordBtn.enabled = isCanPlayRecord (a recording exists) (VC.m:161-166)
//   speed cycle: 1.0 → 0.75 → 1.25 → 1.0                     (VC.m:466-477 changeSpeed)
//   speed label: "Speed 1x" / "Speed 0.75x" / "Speed 1.25x" (VC.m:480-490 updateSpeedText)
//
// ASCII layout (mirrors the .xib stack — slider on top, button row,
// counter labels at the bottom):
//
//   ┌────────────────────────────────────────────────┐
//   │  ◄──────────────●───────────────►   (volume)    │   soundSlider
//   │   ⏮     ▶      ⏺      ▶|     ⏭      [Speed 1x]   │   5 buttons + speed
//   │  Num:1/11                     Play so far:0/1    │   numLabel | playSoFarLabel
//   └────────────────────────────────────────────────┘
//
// WEB-PLATFORM NOTES (Rule #9 "technically not possible → substitute"):
//   • iOS uses bundled PNG glyph assets (SelfRecordingPlaySound@2x.png etc.);
//     the web build has no asset pipeline for these, so we use Unicode
//     transport glyphs. The control SEMANTICS mirror canon exactly.
//   • The iOS record flow has a confirm/cancel modal step
//     (KFERecordingControlDelegate startRecord/confirmRecord/cancelRecord);
//     the web MediaRecorder auto-confirms on stop, so record collapses to a
//     start/stop toggle (record:start ⇄ record:stop). See kfe-self-record.js.
//
// This component is PRESENTATIONAL: it holds no recorder/audio state. The
// host (kfe-self-record) owns all state and passes a snapshot via the
// `state` prop; the bar renders it and emits intent events. Same
// screen↔engine discipline as the rest of the app (CLAUDE.md Rules #1-3).
//
// Emits (composed, bubbling):
//   nav:prev               ⏮ tapped
//   nav:next               ⏭ tapped
//   playback:model         ▶ (model audio) tapped
//   playback:recording     ▶| (your recording) tapped
//   record:start           ⏺ tapped while idle
//   record:stop            ⏺ tapped while recording
//   volume:change  {value} slider moved (0..1)
//   speed:change   {speed} speed button tapped — detail is the NEXT speed

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";

/** The canon speed cycle (KFESelfRecordingVC.m:466-477). */
export const SPEED_CYCLE = Object.freeze([1.0, 0.75, 1.25]);

/**
 * Next speed in the iOS cycle 1.0 → 0.75 → 1.25 → 1.0. Anything off-cycle
 * resets to 1.0 (defensive — iOS only ever holds one of the three).
 * @param {number} speed
 * @returns {number}
 */
export function nextSpeed(speed) {
  const i = SPEED_CYCLE.indexOf(speed);
  if (i === -1) return 1.0;
  return SPEED_CYCLE[(i + 1) % SPEED_CYCLE.length];
}

/**
 * @typedef {object} ControlBarState
 * @property {boolean} [isRecording]    record is mid-capture (toggle stop)
 * @property {boolean} [hasRecording]   a recording exists for the current word
 * @property {number}  [num]            current word index (1-based)
 * @property {number}  [total]          total words in the episode
 * @property {number}  [playSoFar]      this word's completed plays
 * @property {number}  [mustPlay]       this word's required plays
 * @property {number}  [volume]         0..1
 * @property {number}  [speed]          1.0 | 0.75 | 1.25
 * @property {boolean} [canPrev]        previous-word button enabled
 * @property {boolean} [canNext]        next-word button enabled
 */

export class KFERecordingControlBar extends KFEComponent {
  static props = ["state"];
  static styles = `
    :host { display: block; }
    .bar { background: var(--kfe-surface); border: 1px solid var(--kfe-border);
           border-radius: 12px; padding: 0.75rem; display: flex;
           flex-direction: column; gap: 0.6rem; }
    .slider { width: 100%; }
    input[type="range"] { width: 100%; accent-color: var(--kfe-accent); }
    .buttons { display: flex; align-items: center; justify-content: space-between;
               gap: 0.4rem; }
    .buttons button { flex: 1 1 0; min-width: 0; padding: 0.5rem 0;
            border-radius: 10px; border: 1px solid var(--kfe-border);
            background: var(--kfe-bg); color: var(--kfe-fg); cursor: pointer;
            font-size: 1.35rem; line-height: 1; }
    .buttons button.record { font-size: 1.5rem; }
    .buttons button.record.recording { background: #b22; color: #fff; border: 0; }
    .buttons button[disabled] { opacity: 0.4; cursor: default; }
    .speed { flex: 0 0 auto; font-size: 0.8rem !important; padding: 0.5rem 0.6rem !important;
             white-space: nowrap; }
    .counters { display: flex; justify-content: space-between;
                font-size: 0.8rem; color: var(--kfe-muted); }
  `;

  /** @returns {ControlBarState} */
  _state() {
    return /** @type {any} */ (this).state ?? {};
  }

  render() {
    const s = this._state();
    const isRecording = !!s.isRecording;
    const hasRecording = !!s.hasRecording;
    const speed = typeof s.speed === "number" ? s.speed : 1.0;
    const volume = typeof s.volume === "number" ? s.volume : 1.0;

    const bar = document.createElement("div");
    bar.className = "bar";

    // ── volume scrubber (soundSlider) ──
    const sliderWrap = document.createElement("div");
    sliderWrap.className = "slider";
    const slider = document.createElement("input");
    slider.type = "range";
    slider.min = "0";
    slider.max = "1";
    slider.step = "0.01";
    slider.value = String(volume);
    slider.setAttribute("aria-label", t("sr.volume"));
    slider.addEventListener("input", () =>
      this.emit("volume:change", { value: Number(slider.value) }),
    );
    sliderWrap.appendChild(slider);
    bar.appendChild(sliderWrap);

    // ── 5-button transport row + speed ──
    const row = document.createElement("div");
    row.className = "buttons";

    row.appendChild(
      this._btn("⏮", t("sr.prev"), "prev", !s.canPrev, () => this.emit("nav:prev")),
    );
    row.appendChild(
      this._btn("▶", t("sr.playModel"), "play-model", isRecording, () =>
        this.emit("playback:model"),
      ),
    );

    const rec = this._btn(
      isRecording ? "⏹" : "⏺",
      isRecording ? t("sr.stop") : t("sr.record"),
      "record",
      false,
      () => this.emit(isRecording ? "record:stop" : "record:start"),
    );
    rec.classList.add("record");
    if (isRecording) rec.classList.add("recording");
    row.appendChild(rec);

    row.appendChild(
      this._btn("🔊", t("sr.playRecording"), "play-recording", !hasRecording || isRecording, () =>
        this.emit("playback:recording"),
      ),
    );
    row.appendChild(
      this._btn("⏭", t("sr.nextWord"), "next", !s.canNext, () => this.emit("nav:next")),
    );

    const speedBtn = document.createElement("button");
    speedBtn.type = "button";
    speedBtn.className = "speed";
    speedBtn.dataset.role = "speed";
    speedBtn.textContent = t("sr.speedLabel", { rate: formatSpeed(speed) });
    speedBtn.addEventListener("click", () =>
      this.emit("speed:change", { speed: nextSpeed(speed) }),
    );
    row.appendChild(speedBtn);

    bar.appendChild(row);

    // ── counter labels (numLabel | playSoFarLabel) ──
    const counters = document.createElement("div");
    counters.className = "counters";
    const num = document.createElement("span");
    num.className = "num";
    num.textContent = t("sr.num", {
      n: String(s.num ?? 1),
      total: String(s.total ?? 1),
    });
    const playSoFar = document.createElement("span");
    playSoFar.className = "play-so-far";
    playSoFar.textContent = t("sr.playSoFar", {
      n: String(s.playSoFar ?? 0),
      total: String(s.mustPlay ?? 1),
    });
    counters.appendChild(num);
    counters.appendChild(playSoFar);
    bar.appendChild(counters);

    return html`${bar}`;
  }

  /**
   * @param {string} glyph
   * @param {string} label
   * @param {string} role
   * @param {boolean} disabled
   * @param {() => void} onClick
   * @returns {HTMLButtonElement}
   */
  _btn(glyph, label, role, disabled, onClick) {
    const b = document.createElement("button");
    b.type = "button";
    b.dataset.role = role;
    b.setAttribute("aria-label", label);
    b.title = label;
    b.textContent = glyph;
    b.disabled = disabled;
    b.addEventListener("click", onClick);
    return b;
  }
}

/** Format a speed for the button label: 1 → "1", 0.75 → "0.75". */
function formatSpeed(speed) {
  return speed === 1.0 ? "1" : String(speed);
}
